import type { MenuItemDef } from 'src/helpers/main_menu';

export const MAIN_MENU_DEFAULT_IDS = ['dash', 'home', 'energy', 'eventLog'] as const;

export const isValidMainMenuIcon = (icon: unknown): icon is string =>
	typeof icon === 'string' &&
	/^(?:fa-thin|fat|fa-solid|fas|fa-regular|far|fa-light|fal) fa-[a-z0-9-]+$/.test(icon);

export type CustomMenuTargetType = 'room' | 'dashboard';
export type CustomMenuItem = {
	id: string;
	target_type: CustomMenuTargetType;
	target_id: string;
	icon: string;
};

export type MainMenuCustomization = {
	order: string[];
	custom_items: CustomMenuItem[];
};

export const MAIN_MENU_PREMIUM_MORE_ITEM: MenuItemDef = {
	id: 'premium',
	href: '/settings/premium',
	icon: 'fas fa-gem',
	label: 'label_premium',
	labelDefault: 'Premium',
};

export const MAIN_MENU_OPTIONAL_ITEMS: MenuItemDef[] = [
	{
		id: 'scenes',
		href: '/home/scenes/-1',
		icon: 'fas fa-film',
		label: 'label_menu_items_scenes',
		labelDefault: 'Automation (Scenes)',
	},
	{
		id: 'allDevices',
		href: '/home/devices/-1',
		icon: 'fas fa-grid-2',
		label: 'label_all_devices',
		labelDefault: 'All Devices',
	},
	{
		id: 'cameraMonitoring',
		href: '/dash/camera_monitoring',
		icon: 'fas fa-video',
		label: 'submenu_tab_camera_monitoring',
		labelDefault: 'Monitoring Center',
	},
	{
		id: 'subscription',
		href: '/settings/premium',
		icon: 'fas fa-credit-card',
		label: 'label_subscriptions_tab',
		labelDefault: 'Subscription',
	},
];

const BUILT_IN_IDS = new Set<string>([
	...MAIN_MENU_DEFAULT_IDS,
	...MAIN_MENU_OPTIONAL_ITEMS.map((item) => item.id ?? ''),
]);

export const getDefaultMainMenuCustomization = (legacyOrder?: unknown): MainMenuCustomization => {
	let parsedOrder = legacyOrder;
	if (typeof legacyOrder === 'string') {
		try {
			parsedOrder = JSON.parse(legacyOrder);
		} catch {
			parsedOrder = null;
		}
	}
	const order = Array.isArray(parsedOrder)
		? [
				...new Set(
					parsedOrder
						.filter(
							(index): index is number =>
								Number.isInteger(index) &&
								index >= 0 &&
								index < MAIN_MENU_DEFAULT_IDS.length,
						)
						.map((index) => MAIN_MENU_DEFAULT_IDS[index]),
				),
			]
		: [];
	for (const id of MAIN_MENU_DEFAULT_IDS) {
		if (!order.includes(id)) order.push(id);
	}
	return { order, custom_items: [] };
};

export const getCustomMenuItemId = (targetType: CustomMenuTargetType, targetId: string) =>
	`custom:${targetType}:${targetId}`;

export const createCustomMenuItem = (
	config: MainMenuCustomization,
	targetType: CustomMenuTargetType | '',
	targetId: string,
	icon: string,
	roomIds: readonly string[],
	dashboardIds: readonly string[],
): { item: CustomMenuItem | null; error: 'invalid' | 'duplicate' | null } => {
	if (
		(targetType !== 'room' && targetType !== 'dashboard') ||
		!targetId ||
		!isValidMainMenuIcon(icon) ||
		!(targetType === 'room' ? roomIds : dashboardIds).includes(targetId)
	) {
		return { item: null, error: 'invalid' };
	}
	const id = getCustomMenuItemId(targetType, targetId);
	if (config.custom_items.some((item) => item.id === id)) {
		return { item: null, error: 'duplicate' };
	}
	return { item: { id, target_type: targetType, target_id: targetId, icon }, error: null };
};

export const updateCustomMenuItem = (
	config: MainMenuCustomization,
	currentId: string,
	targetType: CustomMenuTargetType | '',
	targetId: string,
	icon: string,
	roomIds: readonly string[],
	dashboardIds: readonly string[],
): { config: MainMenuCustomization | null; error: 'invalid' | 'duplicate' | null } => {
	if (!config.custom_items.some((item) => item.id === currentId)) {
		return { config: null, error: 'invalid' };
	}
	const remainingItems = config.custom_items.filter((item) => item.id !== currentId);
	const result = createCustomMenuItem(
		{ ...config, custom_items: remainingItems },
		targetType,
		targetId,
		icon,
		roomIds,
		dashboardIds,
	);
	if (!result.item) return { config: null, error: result.error };
	return {
		config: {
			order: config.order.map((id) => (id === currentId ? result.item.id : id)),
			custom_items: [...remainingItems, result.item],
		},
		error: null,
	};
};

export const parseMainMenuCustomization = (
	value: unknown,
	legacyOrder?: unknown,
): MainMenuCustomization => {
	if (typeof value === 'string') {
		try {
			return parseMainMenuCustomization(JSON.parse(value), legacyOrder);
		} catch {
			return getDefaultMainMenuCustomization(legacyOrder);
		}
	}
	if (!value || typeof value !== 'object') return getDefaultMainMenuCustomization(legacyOrder);

	const data = value as Record<string, unknown>;
	const customItems: CustomMenuItem[] = [];
	if (Array.isArray(data.custom_items)) {
		for (const candidate of data.custom_items) {
			if (!candidate || typeof candidate !== 'object') continue;
			const item = candidate as Record<string, unknown>;
			if (item.target_type !== 'room' && item.target_type !== 'dashboard') continue;
			if (typeof item.target_id !== 'string' || !item.target_id) continue;
			if (!isValidMainMenuIcon(item.icon)) continue;
			const id = getCustomMenuItemId(item.target_type, item.target_id);
			if (item.id !== id || customItems.some((existing) => existing.id === id)) continue;
			customItems.push({
				id,
				target_type: item.target_type,
				target_id: item.target_id,
				icon: item.icon as string,
			});
		}
	}

	const allowedIds = new Set([...BUILT_IN_IDS, ...customItems.map((item) => item.id)]);
	const order = Array.isArray(data.order)
		? data.order.filter((id): id is string => typeof id === 'string' && allowedIds.has(id))
		: getDefaultMainMenuCustomization(legacyOrder).order;
	const uniqueOrder = [...new Set(order)];
	return { order: uniqueOrder, custom_items: customItems };
};

export const validateMainMenuCustomization = (
	config: MainMenuCustomization,
	roomIds: readonly string[],
	dashboardIds: readonly string[],
): boolean => {
	if (!Array.isArray(config.order) || !Array.isArray(config.custom_items)) return false;
	if (new Set(config.order).size !== config.order.length) return false;
	const customIds = new Set<string>();
	for (const item of config.custom_items) {
		if (item.target_type !== 'room' && item.target_type !== 'dashboard') return false;
		if (item.id !== getCustomMenuItemId(item.target_type, item.target_id)) return false;
		if (!isValidMainMenuIcon(item.icon)) return false;
		if (customIds.has(item.id)) return false;
		if (!(item.target_type === 'room' ? roomIds : dashboardIds).includes(item.target_id))
			return false;
		customIds.add(item.id);
	}
	return config.order.every((id) => BUILT_IN_IDS.has(id) || customIds.has(id));
};

export const resolveCustomMenuItem = (
	item: CustomMenuItem,
	rooms: Record<string, { name?: string }>,
	dashboards: Record<string, { name?: string }>,
): MenuItemDef | null => {
	const target = item.target_type === 'room' ? rooms[item.target_id] : dashboards[item.target_id];
	if (!target) return null;
	return {
		id: item.id,
		href:
			item.target_type === 'room'
				? `/home/room/${item.target_id}/devices`
				: `/dash/${item.target_id}`,
		icon: item.icon,
		label: target.name || (item.target_type === 'room' ? 'Room' : 'Dashboard'),
		labelDefault: target.name || (item.target_type === 'room' ? 'Room' : 'Dashboard'),
	};
};
